import React from 'react';
import { Link } from 'react-router-dom';

interface TopicCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  path: string;
}

const RAGTesting: React.FC = () => {
  const topics: TopicCard[] = [
    {
      id: 'foreign-contact-reporting',
      title: 'Foreign Contact Reporting (Original)',
      description: 'Test FAQ system using FINAL SEAD 3 Contact decision matrix (5-page training document)',
      icon: '🌍',
      path: '/rag-test/foreign-contact-reporting'
    },
    {
      id: 'foreign-contact-reporting-sead3',
      title: 'Foreign Contact Reporting (SEAD-3)',
      description: 'Test FAQ system using SEAD-3-Reporting-U.pdf Section F.2 (comprehensive policy)',
      icon: '🌍',
      path: '/rag-test/foreign-contact-reporting-sead3'
    },
    {
      id: 'comparison',
      title: 'Foreign Contact - Side-by-Side Comparison',
      description: 'Compare both versions of Foreign Contact reporting side-by-side with the same questions',
      icon: '⚖️',
      path: '/rag-test/comparison'
    },
    {
      id: 'foreign-travel-reporting',
      title: 'Foreign Travel Reporting',
      description: 'Test FAQ system for unofficial foreign travel reporting, itineraries, and approvals',
      icon: '✈️',
      path: '/rag-test/foreign-travel-reporting'
    },
    {
      id: 'financial-matters-reporting',
      title: 'Financial Matters Reporting',
      description: 'Test FAQ system for bankruptcy, debt delinquency, and financial anomalies',
      icon: '💰',
      path: '/rag-test/financial-matters-reporting'
    },
    {
      id: 'substance-treatment-reporting',
      title: 'Substance Treatment Reporting',
      description: 'Test FAQ system for alcohol and drug treatment reporting requirements',
      icon: '🏥',
      path: '/rag-test/substance-treatment-reporting'
    },
    {
      id: 'personal-relationships-reporting',
      title: 'Personal Relationships Reporting',
      description: 'Test FAQ system for marriage, cohabitation, and adoption reporting',
      icon: '👥',
      path: '/rag-test/personal-relationships-reporting'
    },
    {
      id: 'legal-matters-reporting',
      title: 'Legal Matters Reporting',
      description: 'Test FAQ system for arrests and criminal conduct reporting',
      icon: '⚖️',
      path: '/rag-test/legal-matters-reporting'
    },
    {
      id: 'foreign-activities-reporting',
      title: 'Foreign Activities Reporting',
      description: 'Test FAQ system for foreign citizenship, passports, business, and property',
      icon: '🏢',
      path: '/rag-test/foreign-activities-reporting'
    },
    {
      id: 'media-contact-reporting',
      title: 'Media Contact Reporting',
      description: 'Test FAQ system for media contacts seeking classified information',
      icon: '📰',
      path: '/rag-test/media-contact-reporting'
    },
    {
      id: 'reportable-actions-by-others',
      title: 'Reportable Actions by Others',
      description: 'Test FAQ system for concerning behaviors of colleagues requiring reporting',
      icon: '👀',
      path: '/rag-test/reportable-actions-by-others'
    }
  ];

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">RAG Testing Dashboard</h1>
          <p className="text-gray-600">
            Test the FAQ Assistant system with different SEAD-3 reporting topics. Each page demonstrates
            the FAQ widget integrated into a simple form context.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-blue-900 mb-1">Testing Instructions</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Click on any topic card to test the FAQ Assistant for that topic</li>
                <li>• Try both the pre-generated FAQs and custom situation assessment</li>
                <li>• <strong>A/B Testing:</strong> Foreign Contact has two versions for quality comparison</li>
                <li>• Use the comparison page to test both versions side-by-side</li>
                <li>• Validate that citations reference the correct source documents</li>
                <li>• Test the feedback widget after asking questions</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topics.map((topic) => (
            <Link
              key={topic.id}
              to={topic.path}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-200 hover:border-blue-400"
            >
              <div className="flex items-start mb-4">
                <span className="text-4xl mr-3">{topic.icon}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{topic.title}</h3>
                  <p className="text-sm text-gray-600">{topic.description}</p>
                </div>
              </div>
              <div className="flex items-center text-blue-600 text-sm font-medium">
                <span>Test FAQ System</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* Developer Tools section */}
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Developer Tools</h2>
          <Link
            to="/rag-test/field-context-tuning"
            className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border border-amber-200 hover:border-amber-400 max-w-md"
          >
            <div className="flex items-start mb-3">
              <span className="text-3xl mr-3">⚙️</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900">Field Context Tuning</h3>
                  <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-medium">Dev Only</span>
                </div>
                <p className="text-sm text-gray-600">
                  Interactively edit, live-test, and LLM-validate field context configs.
                  Export a Python diff to promote changes to the Lambda.
                </p>
              </div>
            </div>
            <div className="flex items-center text-amber-700 text-sm font-medium">
              <span>Open Tuning Tool</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>

        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-amber-900 mb-1">Development Note</h3>
              <p className="text-sm text-amber-800">
                These test pages are for RAG system development and testing. Backend processing for topics
                other than "foreign-contact-reporting" requires document processing and FAQ generation from
                SEAD-3-Reporting-U.pdf. See MULTI_TOPIC_EXPANSION_PLAN.md for implementation details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RAGTesting;
